/**
 * Inline direct flow: a single-provider session opened from the
 * application's click.
 *
 * The provider's sign-in cannot render inside a frame and a browser only
 * opens a window from a user's click, so when the application tells the
 * widget the session is single-provider (`open({ container, singleProvider: true })`)
 * and calls `open()` from its own click, the widget opens the provider window
 * right then instead of rendering a tile the user would have to click. The
 * container shows a brandless status line until the window hands the outcome
 * back through the ordinary popup channel. If the browser blocks the window
 * (no click, pop-ups disallowed) `start()` reports it and the caller falls
 * back to the inline tile.
 */
import type { AlterError, ConnectCompletion, Grant } from "../types";
import { INLINE_MOUNT_ATTR } from "./embed-handler";
import { OAuthHandler } from "./handler";

/**
 * Inline styles for the status panel. Everything inherits from the host page
 * (colour, font family, background) so the panel reads correctly on a dark
 * host as well as a light one, and the only fixed values are structural.
 * Longhands, not the `font` shorthand: a CSS-wide keyword such as `inherit`
 * is not valid inside a shorthand, and a browser drops the whole declaration
 * when it meets one.
 */
const PANEL_STYLE =
  "display:flex;flex-direction:column;gap:12px;padding:16px;" +
  "border:1px solid rgba(128,128,128,0.35);border-radius:8px;" +
  "background-color:transparent;color:inherit;" +
  "font-size:14px;line-height:1.5;font-family:inherit;";

/**
 * Buttons are at least 44 by 44 CSS px, the WCAG 2.5.8 / Apple HIG touch
 * target floor, so they can be hit on a phone inside a small container.
 */
const BUTTON_STYLE =
  "min-height:44px;min-width:44px;padding:10px 16px;border-radius:6px;" +
  "border:1px solid rgba(128,128,128,0.5);background-color:transparent;" +
  "color:inherit;cursor:pointer;font:inherit;";

export interface DirectWindowHandlerOptions {
  baseURL: string;
  container: HTMLElement;
  popupWidth: number;
  popupHeight: number;
  onSuccess: (grants: Grant[], completion: ConnectCompletion) => void;
  onError: (error: AlterError) => void;
  onCancel: () => void;
  onNotice?: (eventName: string, metadata: Record<string, unknown>) => void;
  debug?: boolean;
}

export class DirectWindowHandler {
  private readonly oauth: OAuthHandler;
  private readonly container: HTMLElement;
  private panel: HTMLElement | null = null;
  private readonly onCancel: () => void;

  constructor(options: DirectWindowHandlerOptions) {
    this.container = options.container;
    this.onCancel = options.onCancel;
    // Like every flow handler, this one tears its own UI down before it
    // reports a terminal; the SDK only drops its reference afterwards.
    this.oauth = new OAuthHandler({
      baseURL: options.baseURL,
      popupWidth: options.popupWidth,
      popupHeight: options.popupHeight,
      onSuccess: (grants, completion) => {
        this.removePanel();
        options.onSuccess(grants, completion);
      },
      onError: (error) => {
        this.removePanel();
        options.onError(error);
      },
      onCancel: () => {
        this.removePanel();
        options.onCancel();
      },
      onNotice: options.onNotice,
      debug: options.debug,
    });
  }

  /**
   * Open the provider window from the current click. Returns `"embed"` when
   * the window opened (the flow now runs from the container), `null` when the
   * browser refused it, in which case nothing was rendered.
   */
  start(url: string): "embed" | null {
    if (!this.oauth.tryOpenWindow(url)) {
      return null;
    }
    this.renderPanel();
    return "embed";
  }

  /** Close the window, remove the status line, stop listening. Idempotent. */
  close(): void {
    this.oauth.close();
    this.removePanel();
  }

  private removePanel(): void {
    if (this.panel) {
      if (this.panel.isConnected) {
        this.panel.remove();
      }
      this.panel = null;
    }
  }

  // The status line is deliberately plain: the single-provider rule is no
  // Alter branding on any page, and this is the only thing the container
  // shows while the provider window is open.
  private renderPanel(): void {
    const panel = document.createElement("div");
    panel.setAttribute(INLINE_MOUNT_ATTR, "direct");
    panel.setAttribute("role", "status");
    panel.style.cssText = PANEL_STYLE;

    const text = document.createElement("p");
    text.style.margin = "0";
    text.textContent = "Continue in the window that just opened.";

    const actions = document.createElement("div");
    actions.style.cssText = "display:flex;gap:8px;flex-wrap:wrap;";

    const show = document.createElement("button");
    show.type = "button";
    show.textContent = "Show the window";
    show.setAttribute("data-alter-connect-action", "show");
    show.style.cssText = BUTTON_STYLE;
    show.addEventListener("click", () => this.oauth.focusWindow());

    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.textContent = "Cancel";
    cancel.setAttribute("data-alter-connect-action", "cancel");
    cancel.style.cssText = BUTTON_STYLE;
    cancel.addEventListener("click", () => {
      this.close();
      this.onCancel();
    });

    actions.appendChild(show);
    actions.appendChild(cancel);
    panel.appendChild(text);
    panel.appendChild(actions);
    this.container.appendChild(panel);
    this.panel = panel;
  }
}
